import type { DocumentStatus } from '@indutrust/factory-model';

/**
 * Map a {@link DocumentStatus} to a PrimeNG `p-tag` severity value.
 *
 * @example
 * const severity = getDocumentStatusSeverity('validated'); // "success"
 */
export function getDocumentStatusSeverity(
  status: DocumentStatus | undefined
): 'secondary' | 'warn' | 'success' | 'danger' {
  switch (status) {
    case 'validated':
      return 'success';
    case 'refused':
      return 'danger';
    case 'waiting':
    case undefined:
    default:
      return 'warn';
  }
}

/**
 * Map a {@link DocumentStatus} to a PrimeIcons icon name (without `pi-`).
 *
 * @example
 * const icon = getDocumentStatusIcon('validated'); // "verified"
 */
export function getDocumentStatusIcon(
  status: DocumentStatus | undefined
): string {
  switch (status) {
    case 'validated':
      return 'verified';
    case 'refused':
      return 'times-circle';
    case 'waiting':
    case undefined:
    default:
      return 'hourglass';
  }
}

/**
 * Map a file name or MIME type to a PrimeIcons icon name (without `pi-`).
 *
 * Covers the most common document formats encountered on an industrial
 * supplier's profile; falls back to `file` for anything unrecognised.
 *
 * @example
 * getDocumentIcon('report.pdf')           // "file-pdf"
 * getDocumentIcon(undefined, 'image/png') // "image"
 */
export function getDocumentIcon(
  name: string | undefined,
  mime?: string
): string {
  const ext = name?.split('.').pop()?.toLowerCase();

  if (ext === 'pdf' || mime === 'application/pdf') {
    return 'file-pdf';
  }
  if (
    ext === 'doc' ||
    ext === 'docx' ||
    mime?.includes('wordprocessingml') ||
    mime === 'application/msword'
  ) {
    return 'file-word';
  }
  if (
    ext === 'xls' ||
    ext === 'xlsx' ||
    mime?.includes('spreadsheetml') ||
    mime === 'application/vnd.ms-excel'
  ) {
    return 'file-excel';
  }
  if (
    ext === 'png' ||
    ext === 'jpg' ||
    ext === 'jpeg' ||
    ext === 'gif' ||
    ext === 'webp' ||
    ext === 'svg' ||
    mime?.startsWith('image/')
  ) {
    return 'image';
  }
  return 'file';
}

/**
 * Format a file size in bytes to a human-readable string with one decimal
 * place, using the largest applicable unit (B → KB → MB → GB).
 *
 * @example
 * formatFileSize(0)        // "0 B"
 * formatFileSize(1024)     // "1.0 KB"
 * formatFileSize(204800)   // "200.0 KB"
 * formatFileSize(1048576)  // "1.0 MB"
 */
export function formatFileSize(bytes: number | undefined): string {
  if (!bytes || bytes <= 0) {
    return '0 B';
  }
  const units = ['B', 'KB', 'MB', 'GB'];
  let value = bytes;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex++;
  }
  const formatted = unitIndex === 0 ? String(value) : value.toFixed(1);
  return `${formatted} ${units[unitIndex]}`;
}
